import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { selectCurrentUser } from 'ducks/user.duck';
import { isProviderUser } from 'util/userHelpers';
import { getUserSlug } from '../../../profile-slug/utils/users';

export const POSITIONS = ['list-a-class', 'connect-stripe', 'personalise-profile', 'share-profile'];

const STEP_ROUTE_MATCH = {
  'list-a-class':        (p) => p.startsWith('/book/draft/'),
  'connect-stripe':      (p) => p.startsWith('/organisation/payments'),
  'personalise-profile': (p) => p.startsWith('/organisation') && !p.startsWith('/organisation/payments'),
  'share-profile':       (p) => p.startsWith('/u/') || p.startsWith('/u-old/'),
};

const STEP_DEFINITIONS = [
  { id: 'list-a-class', title: 'List your first class', description: 'Create a listing so parents can find and book with you.' },
  { id: 'connect-stripe', title: 'Connect your Stripe account', description: 'Set up payouts so you can start taking bookings.' },
  { id: 'personalise-profile', title: 'Personalise your public profile', description: 'Add a profile photo so parents can get to know you.' },
  { id: 'share-profile', title: 'Share your profile with your audience', description: "Let your audience know you're on Classhoppa!" },
];

const getStepStatus = (stepId, currentPosition) => {
  const currentIndex = POSITIONS.indexOf(currentPosition);
  const stepIndex = POSITIONS.indexOf(stepId);
  if (currentIndex < 0 || stepIndex < 0) return 'locked';
  if (stepIndex < currentIndex) return 'done';
  if (stepIndex === currentIndex) return 'active';
  return 'locked';
};

const useOnboardingSteps = ({ onAction } = {}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentUser = useSelector(selectCurrentUser);
  const onboardingPosition = currentUser?.attributes?.profile?.publicData?.onboardingPosition;

  const isActive = isProviderUser(currentUser) && POSITIONS.includes(onboardingPosition);

  const stepActions = {
    'list-a-class': () => { onAction?.(); navigate('/book/draft/00000000-0000-0000-0000-000000000000/new/details?first-time=true'); },
    'connect-stripe': () => { onAction?.(); navigate('/organisation/payments'); },
    'personalise-profile': () => { onAction?.(); navigate('/organisation'); },
    'share-profile': () => {
      onAction?.();
      const slug = getUserSlug(currentUser);
      navigate(slug ? `/u/${slug}` : `/u-old/${currentUser.id.uuid}`);
    },
  };

  const steps = STEP_DEFINITIONS.map((step) => ({
    ...step,
    status: getStepStatus(step.id, onboardingPosition),
    isInProgress: step.id === onboardingPosition && !!STEP_ROUTE_MATCH[step.id]?.(pathname),
    onAction: step.id === onboardingPosition ? stepActions[step.id] : undefined,
  }));

  return { isActive, steps, onboardingPosition };
};

export default useOnboardingSteps;
